import * as React from 'react';

interface ReportTypePreviewHighlight {
	virtualIds: Record<string, true>;
	focusedVirtualId: Nullable<string>;
	highlight: (virtualIds: string[], focusedVirtualId?: string) => void;
	clear: () => void;
}

export const ReportTypePreviewHighlightContext = React.createContext<Nullable<ReportTypePreviewHighlight>>(null);

export function useReportTypePreviewHighlightContext() {
	const context = React.useContext(ReportTypePreviewHighlightContext);
	if (!context) {
		throw new Error('ReportTypePreviewHighlightContext not initialized');
	}
	return context;
}

export function useReportTypePreviewHighlightContextState() {
	const [virtualIds, setVirtualIds] = React.useState<string[]>([]);
	const [focusedVirtualId, setFocusedVirtualId] = React.useState<Nullable<string>>(null);

	const virtualIdMap = React.useMemo(() => {
		return virtualIds.reduce<Record<string, true>>((_acc, _id) => {
			_acc[_id] = true;
			return _acc;
		}, {});
	}, [virtualIds]);

	const highlight = React.useCallback((nextVirtualIds: string[], nextFocusedVirtualId?: string) => {
		setVirtualIds(nextVirtualIds);
		setFocusedVirtualId(nextFocusedVirtualId ?? null);
	}, []);

	const clear = React.useCallback(() => {
		setVirtualIds([]);
		setFocusedVirtualId(null);
	}, []);

	return React.useMemo<ReportTypePreviewHighlight>(() => {
		return {
			virtualIds: virtualIdMap,
			focusedVirtualId,
			highlight,
			clear,
		};
	}, [clear, focusedVirtualId, highlight, virtualIdMap]);
}
